import { onBeforeUnmount, watch } from 'vue';

import { trackAnalyticsEvent, useAnalyticsConsent } from '@/composables/useAnalytics';
import { buildPageViewPayload } from '@/utils/pageViewEvent';

export const PAGE_PRESENCE_INTERVAL_MS = 60_000;

/**
 * Emit consent-gated heartbeats for routes that opt in through
 * `meta.analytics.presence`. Only visible tabs count as active.
 */
export function usePagePresence(route, { intervalMs = PAGE_PRESENCE_INTERVAL_MS } = {}) {
  const { consent } = useAnalyticsConsent();
  let timer = null;

  function stop() {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  }

  function payload() {
    const analytics = route?.meta?.analytics;
    if (!analytics?.presence || !analytics.toolId) {
      return null;
    }
    return {
      ...buildPageViewPayload(route),
      tool_id: String(analytics.toolId),
    };
  }

  function emit() {
    const event = payload();
    if (event) {
      trackAnalyticsEvent('page.heartbeat', event);
    }
  }

  function restart() {
    stop();
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible' ||
      consent.value !== 'granted' ||
      !payload()
    ) {
      return;
    }
    emit();
    timer = window.setInterval(emit, intervalMs);
  }

  const stopRouteWatch = watch(
    () => [route?.name, route?.meta?.analytics?.toolId, route?.meta?.analytics?.presence],
    restart,
    { immediate: true },
  );
  const stopConsentWatch = watch(consent, restart);

  document.addEventListener('visibilitychange', restart);
  onBeforeUnmount(() => {
    stop();
    stopRouteWatch();
    stopConsentWatch();
    document.removeEventListener('visibilitychange', restart);
  });
}
